"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import type { IconType } from "react-icons";
import { FaCheckCircle, FaGoogle, FaPhone, FaStar } from "react-icons/fa";
import { SiFacebook, SiHomeadvisor, SiYelp } from "react-icons/si";
import ContactForm from "./ContactForm";
import Navbar from "./Navbar";
import type { CityLocation } from "@/data/locations";

export type HeroContent = {
  eyebrow?: string;
  heading?: string;
  intro?: string;
  bullets?: string[];
  /**
   * Review/directory tiles under the hero CTAs: `true` uses the default
   * (Houston/company) profiles; a list overrides them per page. A tile
   * without `href` is shown but not linked.
   */
  directories?: boolean | DirectoryProfile[];
};

// Public directory profiles supplied by the client. Only Google shows a
// rating (pulled live from /api/reviews); the rest just link to the profile.
/** A directory tile: name + optional link. Icons/colours come from `directoryBrands`. */
export type DirectoryProfile = { name: DirectoryName; href?: string };
type DirectoryName = "Google" | "Facebook" | "Yelp" | "HomeAdvisor" | "Prospeo";

// Kept inside this client component so server pages only pass plain data.
const directoryBrands: Record<DirectoryName, { icon?: IconType; iconColor: string; mark?: string }> = {
  Google: { icon: FaGoogle, iconColor: "#4285F4" },
  Facebook: { icon: SiFacebook, iconColor: "#1877F2" },
  Yelp: { icon: SiYelp, iconColor: "#D32323" },
  HomeAdvisor: { icon: SiHomeadvisor, iconColor: "#F68315" },
  Prospeo: { iconColor: "#1C1C1C", mark: "P" },
};

export const directoryProfiles: DirectoryProfile[] = [
  { name: "Google", href: "https://www.google.com/maps?cid=15771268223932172925" },
  { name: "Facebook", href: "https://www.facebook.com/profile.php?id=61551671987415" },
  { name: "Yelp", href: "https://www.yelp.com/biz/ruff-roofing-houston" },
  { name: "HomeAdvisor", href: "https://www.homeadvisor.com/rated.RuffRoofing.132798631.html" },
  { name: "Prospeo", href: "https://prospeo.io/c/ruff-roofing" },
];

export default function Header({
  city,
  hero,
  reviewSummary,
}: {
  city?: CityLocation;
  hero?: HeroContent;
  /** Fixed review figures for a city office; omit rating to show the count only. */
  reviewSummary?: { rating?: number; total: number };
}) {
  const heading = hero?.heading ?? city?.heroHeading ?? "Texas\u2019 Trusted Roofing Experts \u2014 Houston, Dallas, Austin, San Antonio & More";
  const sub = hero?.intro ?? city?.heroSub ?? "Built Tough. Backed by Texans. Your Roof, Done Right.";
  const phoneHref = city?.phone ?? "+13463393405";
  const phoneLabel = city?.phoneFormatted ?? "(346) 339-3405";

  const [reviewStats, setReviewStats] = useState({
    rating: 4.8,
    total: 468,
    source: "Google",
  });

  // Defer the reviews fetch \u2014 it's below the fold and the endpoint is currently
  // 502-ing while billing is disabled, so there's no point making it part of
  // the critical request chain. Use rIC where available so it cedes to FCP.
  useEffect(() => {
    const run = () => {
      fetch("/api/reviews")
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.rating && data?.total) {
            setReviewStats({
              rating: data.rating,
              total: data.total,
              source: data.source ?? "Google",
            });
          }
        })
        .catch(() => {});
    };
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof ric === "function") {
      ric(run, { timeout: 3000 });
    } else {
      window.setTimeout(run, 2000);
    }
  }, []);

  return (
    <>
      <Navbar />
      <section className="relative w-full min-h-[80svh] overflow-hidden flex flex-col bg-black">
        {/* ── Background Video ── */}
        {/* object-position: adjust "center 30%" to move the video crop up/down */}
        {/* 0% = top of video, 50% = center, 100% = bottom */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/images/optimized/hero-poster.webp"
          width={1920}
          height={1080}
          suppressHydrationWarning
          className="absolute inset-0 h-full w-full object-cover object-bottom [transform:translateY(-17%)] sm:[transform:none] sm:object-[center_56%]"
        >
          <source src="/videos/cinematic-reel.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay */}
        <div className="absolute inset-0 z-[1] bg-black/50" />

        {/* ── Hero Content ── */}
        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col justify-center items-center lg:items-start px-6 pt-48 pb-32 lg:pt-56 lg:pb-40 text-center lg:text-left flex-1">
          {hero?.eyebrow && (
            <p className="mb-5 inline-flex rounded-full bg-[#DC3332] px-4 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-black/20">
              {hero.eyebrow}
            </p>
          )}
          <h1 className="font-['Outfit'] text-[32px] sm:text-[38px] md:text-6xl lg:text-[72px] leading-[1.02] font-extrabold text-white tracking-tight max-w-4xl">
            {heading}
          </h1>
          <p className={`mt-5 text-base sm:text-lg md:text-xl text-white/80 font-light ${hero?.intro ? "max-w-3xl leading-relaxed" : "max-w-2xl"}`}>
            {sub}
          </p>
          {hero?.bullets && hero.bullets.length > 0 && (
            <ul className="mt-6 grid w-full max-w-2xl grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-left">
              {hero.bullets.map((bullet) => (
                <li key={bullet} className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-white">
                  <FaCheckCircle className="shrink-0 text-[#DC3332]" />
                  {bullet}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3 sm:gap-4 w-full sm:w-auto">
            <a
              href="#hero-contact-form"
              data-ph-event="hero_cta_clicked"
              data-ph-label="Get Free Estimate"
              data-ph-location="homepage_hero"
              onClick={(e) => {
                const el = document.getElementById("hero-contact-form");
                if (el) {
                  e.preventDefault();
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              className="inline-flex items-center justify-center gap-2 bg-[#DC3332]/50 backdrop-blur-xl text-white rounded-full px-8 py-3.5 text-sm font-semibold uppercase tracking-wide hover:bg-[#DC3332]/70 transition-colors border border-white/20 shadow-lg min-h-[48px]"
            >
              Get Free Estimate
            </a>
            <a
              href={`tel:${phoneHref}`}
              data-ph-location="homepage_hero"
              data-ph-label={phoneLabel}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white rounded-full px-8 py-3.5 text-sm font-semibold uppercase tracking-wide hover:bg-white/10 transition-colors min-h-[48px]"
            >
              <FaPhone className="text-xs" />
              {phoneLabel}
            </a>
          </div>
          {hero?.directories && (
            <ul className="mt-8 grid w-full max-w-2xl grid-cols-2 gap-2.5 sm:grid-cols-3 text-left">
              {(hero.directories === true ? directoryProfiles : hero.directories).map(({ name, href }) => {
                const { icon: Icon, iconColor, mark } = directoryBrands[name];
                const tileClass =
                  "flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/15 px-2.5 py-2 backdrop-blur-md";
                const googleCountOnly = reviewSummary && reviewSummary.rating === undefined;
                const body = (
                  <>
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-lg font-extrabold"
                      style={{ color: iconColor }}
                    >
                      {Icon ? <Icon /> : <span className="font-['Outfit'] text-[11px] tracking-tight">{mark}</span>}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-white">{name}</span>
                      {name === "Google" ? (
                        googleCountOnly ? (
                          <span className="block text-[11px] text-white/80">{reviewSummary.total} Google reviews</span>
                        ) : (
                          <span className="flex items-center gap-1 text-[11px] text-white/80">
                            <span className="flex text-[10px] text-amber-400">
                              <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                            </span>
                            {reviewSummary?.rating ?? reviewStats.rating}
                          </span>
                        )
                      ) : href ? (
                        <span className="block text-[11px] text-white/70">View profile</span>
                      ) : null}
                    </span>
                  </>
                );
                return (
                  <li key={name}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-ph-event="directory_profile_clicked"
                        data-ph-label={name}
                        data-ph-location={city ? `${city.slug}_hero` : "homepage_hero"}
                        className={`${tileClass} transition-colors hover:bg-white/25`}
                      >
                        {body}
                      </a>
                    ) : (
                      <div className={tileClass}>{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Full-width bar: Trust badges (left) + Form (right) */}
        <div className="relative z-40 mt-auto">
          {/* Sleeping dog mascot — desktop */}
          <div className="hidden lg:block absolute -top-62 right-0 xl:right-0 2xl:right-48 z-50 pointer-events-none">
            <Image
              src="/images/optimized/dog-sleeping-white.webp"
              alt="Ruff Roofing mascot"
              width={540}
              height={335}
              sizes="540px"
              className="object-contain w-auto h-auto"
            />
          </div>
          {/* Sleeping dog mascot — mobile (same image, scaled down, anchored above the form bar on the right) */}
          <div className="lg:hidden absolute -top-[138px] -right-10 z-50 pointer-events-none">
            <Image
              src="/images/optimized/dog-sleeping-white.webp"
              alt="Ruff Roofing mascot"
              width={300}
              height={200}
              sizes="300px"
              className="object-contain w-auto h-auto"
            />
          </div>
          {/* Black divider line */}
          <div className="absolute -top-[3px] left-0 right-0 h-[6px] bg-[#1C1C1C] z-40" />
          <div className="bg-white shadow-2xl shadow-black/20">
            <div className="flex flex-col lg:flex-row items-stretch max-w-[1440px] mx-auto">
              {/* Left (desktop) / Top (mobile) — Trust & Social Proof */}
              <div className="order-1 flex lg:w-[42%] flex-col justify-center items-center px-4 sm:px-8 lg:px-10 xl:px-14 pt-4 pb-0 lg:py-0 lg:-mt-4 relative text-center gap-0.5 lg:gap-0">
                {/* Vertical divider — desktop only */}
                <div className="hidden lg:block absolute right-0 top-[10%] bottom-[10%] w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />

                <p className="text-lg sm:text-xl lg:text-2xl font-extrabold uppercase tracking-[0.2em] text-[#DC3332] font-['Outfit']">Trusted &amp; Certified</p>

                {/* Mobile: badge 60% left + stats 40% right | Desktop: stacked (contents) */}
                <div className="grid grid-cols-[3fr_2fr] items-center gap-3 sm:gap-4 w-full lg:contents">

                  {/* Owens Corning 2026 Top Performer */}
                  <a
                    href="https://www.owenscorning.com/en-us/roofing/contractors/contractor-profile/240470"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-ph-event="trust_badge_clicked"
                    data-ph-label="Owens Corning 2026"
                    data-ph-location="homepage_hero_trust_bar"
                    className="flex items-center justify-center hover:opacity-80 transition-opacity lg:block"
                  >
                    <Image
                      src="/images/optimized/owens-corning-2026.webp"
                      alt="2026 Owens Corning Platinum Preferred Contractor — Top Performer, Platinum Warranty"
                      width={480}
                      height={380}
                      className="object-contain h-auto w-full max-w-[210px] lg:max-w-none lg:w-[240px]"
                    />
                  </a>

                  {/* Stats — vertical stack on mobile (compact), horizontal on desktop */}
                  <div className="lg:w-[90%] rounded-2xl bg-gradient-to-br from-[#F7F7F7] to-[#EDEDED] lg:bg-[#F5F5F5] lg:bg-none px-2 sm:px-3 lg:px-6 py-2 sm:py-2.5 lg:py-4 flex flex-col lg:flex-row items-center justify-center gap-1 lg:gap-6 ring-1 ring-black/5 lg:ring-0 shadow-sm lg:shadow-none">
                    <div className="text-center">
                      <p className="text-[20px] sm:text-[22px] lg:text-[36px] font-extrabold text-[#1C1C1C] leading-none font-['Outfit']">2,500<span className="text-[#DC3332]">+</span></p>
                      <p className="text-[8px] sm:text-[9px] lg:text-[10px] text-gray-500 lg:text-gray-400 uppercase tracking-[0.12em] lg:tracking-[0.15em] mt-1 lg:mt-2 font-semibold lg:font-medium">Roofs Completed</p>
                    </div>
                    <div className="h-px w-3/4 lg:h-12 lg:w-px bg-gray-300/70 lg:bg-gray-300/60 my-0.5 lg:my-0" />
                    {reviewSummary && reviewSummary.rating === undefined ? (
                      // Count only (e.g. a newer office listing without a rating to show).
                      <div className="text-center">
                        <p className="text-[20px] sm:text-[22px] lg:text-[36px] font-extrabold text-[#1C1C1C] leading-none font-['Outfit']">{reviewSummary.total}</p>
                        <p className="text-[8px] sm:text-[9px] lg:text-xs text-[#1C1C1C] uppercase tracking-[0.1em] lg:tracking-[0.15em] mt-1 lg:mt-2 font-bold">
                          Google Reviews
                        </p>
                      </div>
                    ) : (
                    <div className="text-center">
                      <div className="flex justify-center text-amber-400 text-[10px] sm:text-[11px] lg:text-[17px] gap-0.5 mb-0 lg:mb-1.5">
                        <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                      </div>
                      <p className="text-[16px] sm:text-[18px] lg:text-[28px] font-extrabold text-[#1C1C1C] leading-none font-['Outfit']">{reviewSummary?.rating ?? reviewStats.rating}<span className="text-gray-400 lg:text-gray-300 font-medium text-[11px] sm:text-xs lg:text-lg"> / 5</span></p>
                      <p className="text-[8px] sm:text-[9px] lg:text-xs text-[#1C1C1C] uppercase tracking-[0.1em] lg:tracking-[0.15em] mt-1 lg:mt-2 font-bold">
                        {reviewSummary?.total ?? reviewStats.total} {reviewSummary || reviewStats.source === "Google" ? "Google" : "Public"} Reviews
                      </p>
                    </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right — Contact Form */}
              <div id="hero-contact-form" className="order-2 flex-1 -mt-5 lg:mt-0 lg:bg-white lg:rounded-tr-[2.5rem] scroll-mt-24">
                <ContactForm variant="light" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
